import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Fab, Paper, TextField, IconButton, Typography, Avatar, CircularProgress, Slide, Card, CardContent, Button, Chip, Stack, useTheme, useMediaQuery } from '@mui/material';
import { School as TutorIcon, Close as CloseIcon, Send as SendIcon, Add as AddIcon, Check as CheckIcon, SmartToy as BotIcon, Reply as ReplyIcon, EditNote as JournalIcon } from '@mui/icons-material';
import { useLanguage } from '../context/LanguageContext';
import { useTutorContext } from '../context/TutorContext';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';

// Context-aware quick actions. Each returns a ready-made prompt for the tutor.
const getQuickActions = (ctx) => {
  const page = ctx?.page;
  if (page === 'conversation' || page === 'story') {
    return [
      { label: 'Help me reply', prompt: 'Help me come up with a good reply to what was just said.' },
      { label: 'Explain my last mistake', prompt: 'Explain the most recent mistake I made and how to fix it.' },
      { label: 'Make suggestions more me', prompt: 'Take the suggested replies and make them more natural and more specific to me.' },
      { label: 'What did they say?', prompt: 'Explain what the other character just said to me in simpler terms.' },
    ];
  }
  if (page === 'journal') {
    return [
      { label: 'Help me write this', prompt: 'Help me write the sentence I am working on in my journal.' },
      { label: 'Check my grammar', prompt: 'Check the grammar of what I have written so far and explain any fixes.' },
      { label: 'How do I say...', prompt: 'I want to say something but do not know the word. Help me find it.' },
    ];
  }
  return [
    { label: 'Explain a word', prompt: 'Can you explain a word or phrase for me?' },
    { label: 'Quick grammar tip', prompt: 'Give me a quick grammar tip for my level.' },
  ];
};

const TutorAssistant = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectedLanguage, getNativeLanguageName, getLanguageDisplayName } = useLanguage();
  const { tutorContext, insertIntoInput } = useTutorContext();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addedVocab, setAddedVocab] = useState({});
  const messagesEndRef = useRef(null);

  // The learner's level: prefer the active page's context, else their saved
  // level for the selected language. Drives scaffold-vs-answer on the backend.
  const resolveLevel = () => {
    if (tutorContext?.proficiency_level) return tutorContext.proficiency_level;
    const lang = user?.languages?.find(l => l.language_code === selectedLanguage);
    return lang?.proficiency_level || null;
  };

  useEffect(() => {
    if (selectedLanguage) setMessages([{ role: 'assistant', content: `Hello! I'm your ${getLanguageDisplayName(selectedLanguage)} tutor. Ask me anything!` }]);
  }, [selectedLanguage]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, isOpen]);

  const sendMessage = async (text) => {
    if (!text || !text.trim() || !selectedLanguage) return;
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      // Snapshot the current page context and level so help is relevant.
      const response = await apiClient.tutor.chat(
        userMsg.content,
        history,
        selectedLanguage,
        getNativeLanguageName(),
        tutorContext || null,
        resolveLevel()
      );
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        suggested_vocabulary: response.data.suggested_vocabulary,
        suggested_response: response.data.suggested_response,
        suggested_journal: response.data.suggested_journal,
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: "Connection error." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const quickActions = getQuickActions(tutorContext);

  const handleAddVocabulary = async (vocabItem, msgIndex) => {
    try {
      await apiClient.vocabulary.addVocabularyItem({ language_code: selectedLanguage, word: vocabItem.word, translation: vocabItem.translation, context: vocabItem.context, knowledge_level: 1 });
      setAddedVocab(prev => ({ ...prev, [msgIndex]: true }));
    } catch (error) { console.error(error); }
  };

  // Use a tutor-suggested reply: drop it into the active chat input (if a session
  // page is mounted), otherwise copy it to the clipboard as a fallback.
  const handleUseResponse = (text) => {
    const inserted = insertIntoInput(text);
    if (inserted) {
      setIsOpen(false);
    } else if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  };

  // One-click: open the Journal with the suggested draft prefilled (editable).
  const handleAddToJournal = (text) => {
    navigate('/journal', { state: { prefillContent: text } });
    setIsOpen(false);
  };

  if (!selectedLanguage) return null;

  // On mobile, the conversation/story pages have a fixed input bar (with send &
  // mic controls) pinned above the bottom nav. Keep the FAB clear of it:
  //  - session pages: float above the input bar (nav 56 + input ~104 + gap)
  //  - everywhere else: just clear the bottom nav
  // Always add the safe-area inset so it rides above the home indicator.
  const onSessionPage = tutorContext?.page === 'conversation' || tutorContext?.page === 'story';
  const fabBottom = isMobile
    ? (onSessionPage
        ? 'calc(env(safe-area-inset-bottom, 0px) + 96px)'
        : 'calc(env(safe-area-inset-bottom, 0px) + 72px)')
    : 24;
  // On conversation/story pages the send + microphone controls live in the
  // bottom-right, so anchor the tutor to the LEFT there to avoid covering them.
  const fabSide = onSessionPage ? { left: 16 } : { right: 20 };
  const panelSide = onSessionPage ? { left: 0 } : { right: 0 };

  return (
    <Box sx={{ position: 'fixed', bottom: fabBottom, ...fabSide, zIndex: 1300 }}>
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: 'absolute',
            bottom: 72, // stack the panel just above the FAB
            ...panelSide,
            width: isMobile ? 'calc(100vw - 40px)' : 350,
            height: isMobile ? 'calc(100vh - 260px)' : 500,
            maxHeight: isMobile ? 400 : 500,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 3
          }}
        >
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 32, height: 32 }}>
                <BotIcon fontSize="small" />
              </Avatar>
              <Typography variant="subtitle1" fontWeight="bold">AI Tutor</Typography>
            </Box>
            <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
            {messages.map((msg, index) => (
              <Box key={index} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', mb: 2 }}>
                <Box sx={{ maxWidth: '85%' }}>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: msg.role === 'user' ? 'primary.main' : 'white', color: msg.role === 'user' ? 'white' : 'text.primary' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                  </Paper>
                  {msg.suggested_vocabulary && (
                    <Card sx={{ mt: 1, border: '1px solid #e0e0e0' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="primary">{msg.suggested_vocabulary.word}</Typography>
                        <Typography variant="caption" display="block">{msg.suggested_vocabulary.translation}</Typography>
                        <Button
                          size="small"
                          fullWidth
                          startIcon={addedVocab[index] ? <CheckIcon /> : <AddIcon />}
                          variant="outlined"
                          disabled={addedVocab[index]}
                          onClick={() => handleAddVocabulary(msg.suggested_vocabulary, index)}
                          sx={{ mt: 1, fontSize: '0.7rem' }}
                        >
                          {addedVocab[index] ? 'Saved' : 'Add'}
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* One-tap: use a suggested reply */}
                  {msg.suggested_response && (
                    <Card sx={{ mt: 1, border: '1px solid #e0e0e0' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Suggested reply
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>{msg.suggested_response}</Typography>
                        <Button
                          size="small"
                          fullWidth
                          startIcon={<ReplyIcon />}
                          variant="contained"
                          onClick={() => handleUseResponse(msg.suggested_response)}
                          sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                        >
                          Use this reply
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* One-click: add suggested content to the journal */}
                  {msg.suggested_journal && (
                    <Card sx={{ mt: 1, border: '1px solid #e0e0e0' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Journal draft
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{msg.suggested_journal}</Typography>
                        <Button
                          size="small"
                          fullWidth
                          startIcon={<JournalIcon />}
                          variant="outlined"
                          onClick={() => handleAddToJournal(msg.suggested_journal)}
                          sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                        >
                          Add to journal
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Box>
            ))}
            {loading && <CircularProgress size={16} sx={{ ml: 2 }} />}
            <div ref={messagesEndRef} />
          </Box>
          {quickActions.length > 0 && (
            <Box sx={{ px: 1.5, pt: 1, bgcolor: 'white', borderTop: '1px solid #eee' }}>
              <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
                {quickActions.map((action) => (
                  <Chip
                    key={action.label}
                    label={action.label}
                    size="small"
                    onClick={() => sendMessage(action.prompt)}
                    disabled={loading}
                    sx={{ flexShrink: 0 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
          <Box component="form" onSubmit={handleSend} sx={{ p: 1.5, bgcolor: 'white', borderTop: quickActions.length > 0 ? 'none' : '1px solid #eee' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask a question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" type="submit" color="primary" disabled={!input.trim()}>
                    <SendIcon />
                  </IconButton>
                )
              }}
            />
          </Box>
        </Paper>
      </Slide>
      <Fab
        color="primary"
        onClick={() => setIsOpen(!isOpen)}
        size={isMobile ? "medium" : "large"}
        sx={{ boxShadow: 6 }}
      >
        {isOpen ? <CloseIcon /> : <TutorIcon />}
      </Fab>
    </Box>
  );
};

export default TutorAssistant;